import { HttpHandlerFn, HttpInterceptorFn, HttpRequest } from "@angular/common/http";
import { inject } from '@angular/core';
import { catchError, from, switchMap, throwError } from "rxjs";
import { TokenService } from "../services/token.service";

/**
 * Interceptor that adds the auth token to outgoing HTTP requests 
 * and handles token refresh on 401 errors
 */
export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const tokenService = inject(TokenService);
  
  // Skip authentication for login/auth-related requests or non-API requests
  if (req.url.includes('/auth/') || 
      req.url.includes('/.auth/') || 
      req.url.includes('/assets/') || 
      req.url.startsWith('blob:')) {
    return next(req);
  }
  
  // Get the current token from the service
  const token = tokenService.getToken();
  
  // If we have a token, add it to the request
  if (token) {
    const authReq = req.clone({
      headers: req.headers.set('Authorization', `Bearer ${token}`)
    });
    
    // Handle the request with token
    return next(authReq).pipe(
      catchError(error => {
        // If we get a 401, try to refresh the token
        if (error.status === 401) {
         
          
          // Use the token service to refresh
          return from(tokenService.refreshToken()).pipe(
            switchMap(refreshed => {
              if (refreshed) {
                // Get the new token and retry the request
                const newToken = tokenService.getToken();
                if (newToken) {
                  const newAuthReq = req.clone({
                    headers: req.headers.set('Authorization', `Bearer ${newToken}`)
                  });
                  return next(newAuthReq);
                }
              }
              
              // If refresh failed, propagate the error
              return throwError(() => error);
            })
          );
        }
        
        // For other errors, just propagate
        return throwError(() => error);
      })
    );
  }
  
  // No token available, proceed without authentication
  // This could be replaced with a redirection to login if all API calls should be authenticated
  return next(req);
};